
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { WaitlistSection } from "@/components/landing/WaitlistSection";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

const tiers = ["base", "business", "enterprise"];

export const PricingSection = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const sharedFeatures = [
    t("pricing.features.direct_payment_link"),
    t("pricing.features.custom_templates"),
    t("pricing.features.automated_scheduling"),
    t("pricing.features.payment_tracker"),
    t("pricing.features.bulk_scheduling"),
    t("pricing.features.branded_invoice"),
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { error } = await supabase
        .from("waitlist")
        .insert([{ email }]);

      if (error) throw error;

      toast.success(t("waitlist.success"), {
        description: t("waitlist.successMessage"),
      });
      setEmail("");
    } catch (error) {
      console.error("Error:", error);
      toast.error(t("waitlist.error"), {
        description: t("waitlist.errorMessage"),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleButtonClick = (tier: string) => {
    if (tier === 'enterprise') {
      window.location.href = 'mailto:contact@billdrip.com';
    } else {
      navigate("/signup");
    }
  };

  const prices = {
    base: "4",
    business: "9",
    enterprise: t("pricing.contact_us")
  };

  return (
    <motion.section initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }} className="py-16 px-4 bg-gray-50 min-h-screen w-full bg-[radial-gradient(at_75%_60%,#A7DCD8_10%,#8791F3_50%,#F1F6F5_60%)]">
      <div className="max-w-5xl mx-auto">
        <h2 className="text-3xl font-bold text-center mb-4 text-[#34375B]">{t("pricing.title")}</h2>
        <p className="text-xl text-center text-[#34375B]/80 mb-6">{t("pricing.subtitle")}</p>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 w-full">
          {tiers.map((tier, index) => (
            <motion.div key={tier} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 * index }} className="h-full">
              <Card className={`relative h-full flex flex-col border w-full ${tier === 'business' ? 'bg-[#8791F3] text-white' : 'hover:shadow-md'} transition-all duration-300`}>
                <CardHeader className="text-center">
                  <CardTitle className="text-2xl font-bold">{t(`pricing.tiers.${tier}.name`)}</CardTitle>
                </CardHeader>
                <CardContent className="flex-grow text-center">
                  <div className="mb-6">
                    <span className="text-4xl font-bold">
                      {tier === 'enterprise' ? t("navigation.contact_us") : `€${prices[tier as keyof typeof prices]}`}
                      {tier !== 'enterprise' && <span className="text-lg ml-1">{t(`pricing.month`)}</span>}
                    </span>
                  </div>
                  <p className={`text-sm ${tier === 'business' ? 'text-[#F1F6F5]' : 'text-gray-600'}`}>
                    {t(`pricing.tiers.${tier}.invoiceLimit`)}
                  </p>
                  <p className={`text-sm ${tier === 'business' ? 'text-[#F1F6F5]/70' : 'text-gray-400'}`}>
                    {t(`pricing.tiers.free_tier`)}
                  </p>
                </CardContent>
                <CardFooter className="mt-auto pt-4">
                  <Button 
                    onClick={() => handleButtonClick(tier)} 
                    className={`w-full rounded-xl hover:bg-[#5CC2BB] hover:border-0 hover:text-[#34375B] hover:shadow-[4px_4px_0px_0px_#34375B] hover:-translate-x-1 hover:-translate-y-1 transition-all duration-200 ${
                      tier === 'business' ? 'bg-white text-[#8791F3]' : 'bg-white text-gray-900 border border-gray-200'
                    }`}
                  >
                    {tier === 'enterprise' ? t("navigation.contact_us") : t("pricing.getStarted")}
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>
          ))}
        </div>

        <div className="max-w-5xl mx-auto mt-12 p-4 bg-white shadow-md rounded-xl">
          <h3 className="text-xl font-semibold mb-4 text-left text-[#34375B]">{t("pricing.included_in_plans")}</h3>
          <ul className="grid grid-cols-1 md:grid-cols-3 gap-y-4 gap-x-6">
            {sharedFeatures.map((feature) => (
              <li key={feature} className="flex items-center gap-2 text-gray-600">
                <Check className="h-5 w-5 text-[#5CC2BB]" />
                {feature}
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="max-w-2xl mx-auto text-center pt-[100px] pb-[20px]">
        <h2 className="text-3xl font-bold mb-3 text-[#34375B]">
          {t("waitlist.title")}
        </h2>
        <p className="text-xl text-[#34375B]/80 mb-8 font-medium">
          {t("waitlist.subtitle")}
        </p>
        <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
          <Input
            type="email"
            placeholder={t("waitlist.emailPlaceholder")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="flex-grow rounded-xl"
          />
          <Button 
            type="submit" 
            disabled={isLoading}
            className="bg-[#8791F3] hover:shadow-[4px_4px_0px_0px_#34375B] hover:bg-[#5CC2BB] hover:text-[#34375B] hover:border-0 hover:-translate-x-1 hover:-translate-y-1 transition-all duration-200 text-white rounded-xl"
          >
            {isLoading ? t("waitlist.joiningButton") : t("waitlist.joinButton")}
          </Button>
        </form>
      </div>

    </motion.section>
  );
};
